import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface VehicleClassProps {
  baseId: number | null;
}

interface EditableVehicle {
  vehicle_model_id: number | null;
  base_id: number;
  vehicle_model_name: string;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
}

const VehicleClass = ({ baseId }: VehicleClassProps) => {
  const [rows, setRows] = useState<EditableVehicle[]>([]);
  const [originalRows, setOriginalRows] = useState<EditableVehicle[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!baseId) return;

    const fetch = async () => {
      const res = await operatorApiService.doGetVehicleModels({ baseId });
      const mapped = res.map((entry: any) => ({
        ...entry,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.vehicle_model_id}`,
      }));
      setRows(mapped);
      setOriginalRows(mapped);
    };

    fetch();
  }, [baseId]);

  const handleChange = (
    index: number,
    field: keyof EditableVehicle,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        vehicle_model_id: null,
        base_id: baseId!,
        vehicle_model_name: "",
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}`,
      },
    ]);
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? { ...r, isEditing: true, status: "edited" }
          : r
      )
    );
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const handleReset = () => {
    setRows(originalRows.map((r) => ({ ...r })));
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.vehicle_model_name.trim() === ""
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.vehicle_model_name.trim().length > 30
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(({ vehicle_model_name }) => ({
          base_id: baseId!,
          vehicle_model_name,
        }));

      const update = rows
        .filter((r) => r.status === "edited")
        .map(({ vehicle_model_id, vehicle_model_name }) => ({
          vehicle_model_id,
          vehicle_model_name,
        }));

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.vehicle_model_id);

      const response = await operatorApiService.saveVehicleModels({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetVehicleModels({
          baseId,
        });
        const mapped = refreshed.map((entry: any) => ({
          ...entry,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.vehicle_model_id}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  return (
    <div className="vehicleClass">
      <div className="content">
        <div className="title">車種</div>
        <div className="section">
          <div className="sectionTitle">車種一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>車種ID</th>
                  <th>車種名</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.vehicle_model_id ?? "新規"}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.vehicle_model_name}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "vehicle_model_name",
                              e.target.value
                            )
                          }
                        />
                      ) : (
                        row.vehicle_model_name
                      )}
                    </td>
                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規車種を入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleReset}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleClass;
